import { PROTECTED_ROUTES } from 'Utilities/routes';

import { SNOWPLOW_EVENTS } from '../analytics.const';
import type { SnowplowVehicleListGlobalContextProps } from '../analytics.types';

import { createConditionalGlobalContext, getGlobalContext } from './globalContext.helpers';

const { PRO_VEHICLE_LIST } = SNOWPLOW_EVENTS;

export const PROFILE_FEATURE_DISPLAY_NAMES = {
	hasEarlyDocuments: 'Docs received',
	isPaidListing: 'Promoted',
} as const;

type ProfileFeatureKey = keyof typeof PROFILE_FEATURE_DISPLAY_NAMES;
export type ProfileFeatureDisplayName = (typeof PROFILE_FEATURE_DISPLAY_NAMES)[ProfileFeatureKey];

const getSelectedProfileFeatures = (
	filters: Partial<Record<ProfileFeatureKey, boolean>>,
): ProfileFeatureDisplayName[] =>
	(Object.keys(PROFILE_FEATURE_DISPLAY_NAMES) as ProfileFeatureKey[])
		.filter((key) => filters[key] === true)
		.map((key) => PROFILE_FEATURE_DISPLAY_NAMES[key]);

export const snowplowVehicleListGlobalContext = ({
	currentFilter,
	filters,
	hideViewedVehicles,
	listType,
	numVehiclesMatched,
	pathname,
}: SnowplowVehicleListGlobalContextProps): void => {
	const isVehicleListPage = () => pathname === PROTECTED_ROUTES.VEHICLE_LIST;

	const {
		ageFrom,
		ageTo,
		displayPriceFrom,
		displayPriceTo,
		excludeVehiclesOnFinance,
		exteriorGrade,
		fuel,
		isAvailableForCollection,
		make,
		maxDistance,
		mileageFrom,
		mileageTo,
		model,
		numericGrade,
		order,
		previousKeepersCountFrom,
		previousKeepersCountTo,
		sellerType,
		serviceHistory,
		sort,
		transmission,
		vehicleClass,
		vrmOrVin,
	} = filters;

	const vehicleListGlobalContext = getGlobalContext({
		data: {
			filter_id: currentFilter ? `${currentFilter}` : null,
			list_type: listType,
			num_vehicles_matched: numVehiclesMatched,
			selected_age_max: ageTo,
			selected_age_min: ageFrom,
			selected_collection: isAvailableForCollection,
			selected_distance_max: maxDistance,
			selected_exterior_grade: numericGrade || exteriorGrade,
			selected_finance: excludeVehiclesOnFinance,
			selected_fuel: fuel,
			selected_hide_viewed_vehicles: hideViewedVehicles,
			selected_makes: make,
			selected_mileage_max: mileageTo,
			selected_mileage_min: mileageFrom,
			selected_models: model,
			selected_previous_owners_max: previousKeepersCountTo,
			selected_previous_owners_min: previousKeepersCountFrom,
			selected_price_max: displayPriceTo,
			selected_price_min: displayPriceFrom,
			selected_profile_features: getSelectedProfileFeatures(filters),
			selected_seller_type: sellerType,
			selected_service_history: serviceHistory,
			selected_transmission: transmission,
			selected_vehicle_type: vehicleClass,
			selected_vrm_or_vin: vrmOrVin ?? null,
			sort_direction: order,
			sort_order: sort,
		},
		schemaEvent: PRO_VEHICLE_LIST,
	});

	createConditionalGlobalContext({
		conditionalFilter: isVehicleListPage,
		previousGlobalContextDataKey: 'vehicleList',
		updatedGlobalContextData: vehicleListGlobalContext,
	});
};
